/**
 * CartCheckout
 *
 * 購入導線（メイン購入ボタン / フローティングガチャボタン / 10連・無料ガチャ /
 * Portal E-Sensei）で共通の「カートを空にして 1 明細だけ入れ、決済へ進む」
 * 前処理をまとめたもの。
 *
 * 背景: Shopify は /checkout をカートが空の状態で開くとサイト TOP（/）へ
 * 302 させる。従来は /cart/add.js が 200 を返しただけで /checkout へ遷移して
 * いたため、cookie が保持されない端末や、複数ボタン / 複数タブからの二重送信で
 * clear.js と add.js が交錯した場合に「購入ボタンを押したら TOP に飛ばされる」
 * 現象が起きていた。
 *
 * ここでは add.js のあとに /cart.js を取得し、追加したバリエーションが
 * 実際にカートに入っていることを確認してから呼び出し元へ制御を返す。
 * 確認できなければ clear → add をもう 1 回だけやり直し、それでも駄目なら
 * エラーにする（呼び出し元はページに留まってエラー文言を表示する）。
 * 加えて全経路で 1 つの送信ロックを共有し、同時送信を防ぐ。
 *
 * 使い方:
 *   if (window.CartCheckout.busy) return;
 *   try {
 *     await window.CartCheckout.prepare({ variantId, quantity, properties });
 *     window.location.href = '/checkout';
 *   } catch (error) {
 *     showError(error.message);   // error.addData: add.js のエラー本文（あれば）
 *   }
 *
 * prepare() が resolve したら呼び出し元が必ず /checkout へ遷移すること。
 * 遷移後にブラウザバック（bfcache 復帰）した場合はロックを自動解除する。
 */
(function () {
  if (window.CartCheckout) return;

  const ADD_FAILED = 'カートへの追加に失敗しました。もう一度お試しください。';
  const VERIFY_FAILED =
    'カートへの追加を確認できませんでした。ページを再読み込みしてもう一度お試しください。';
  const NETWORK_FAILED = 'ネットワークエラーが発生しました。もう一度お試しください。';

  function makeError(message, addData) {
    const error = new Error(message);
    if (addData) error.addData = addData;
    return error;
  }

  async function clearCart() {
    const response = await fetch('/cart/clear.js', { method: 'POST' });
    if (!response.ok) throw makeError('カートのクリアに失敗しました。もう一度お試しください。');
  }

  async function addItem(item) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [item] }),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok || !data || data.status) {
      const message = (data && (data.description || data.message)) || ADD_FAILED;
      throw makeError(message, data);
    }
    return data;
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', { cache: 'no-store' });
    if (!response.ok) return null;
    return response.json().catch(() => null);
  }

  function cartHasVariant(cart, variantId) {
    if (!cart || !Array.isArray(cart.items) || cart.item_count <= 0) return false;
    return cart.items.some((line) => line.variant_id === variantId);
  }

  const CartCheckout = {
    busy: false,

    /**
     * @param {Object} options
     * @param {number|string} options.variantId
     * @param {number} options.quantity
     * @param {Object|null} [options.properties]  ラインアイテムプロパティ
     * @returns {Promise<Object>} 確認済みの /cart.js レスポンス
     */
    async prepare(options) {
      const variantId = parseInt(options.variantId, 10);
      const quantity = parseInt(options.quantity, 10);
      if (!variantId || !quantity || quantity < 1) {
        throw makeError('商品バリエーションが選択されていません');
      }
      const item = { id: variantId, quantity };
      if (options.properties) item.properties = options.properties;

      this.busy = true;
      try {
        let lastCart = null;
        for (let attempt = 1; attempt <= 2; attempt++) {
          await clearCart();
          await addItem(item);
          lastCart = await fetchCart();
          if (cartHasVariant(lastCart, variantId)) return lastCart;
          console.warn(
            `[CartCheckout] /cart/add.js succeeded but /cart.js does not hold variant ${variantId} (attempt ${attempt})`,
            lastCart
          );
        }
        throw makeError(VERIFY_FAILED);
      } catch (error) {
        this.busy = false;
        if (error instanceof TypeError) {
          // fetch 自体の失敗（オフライン・接続断など）
          throw makeError(NETWORK_FAILED);
        }
        throw error;
      }
    },
  };

  // 決済ページからブラウザバックで戻った（bfcache 復帰）ときにロックを解除
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) CartCheckout.busy = false;
  });

  window.CartCheckout = CartCheckout;
})();
